import React, { useState, useRef, useEffect } from "react";
import { SearchFilterState } from "../../types/appointment";
import { PatientDto } from "../../types/patient";
import { usePatients } from "../../hooks/usePatients";

interface SearchFiltersProps {
  onSearch: (filters: SearchFilterState) => void;
}

const normalizeText = (str: string) =>
  str
    ? str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
    : "";

export const SearchFilters: React.FC<SearchFiltersProps> = ({ onSearch }) => {
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [amka, setAmka] = useState("");
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchParams = {
    ...(lastName.trim() && { lastName: lastName.trim() }),
    ...(firstName.trim() && { firstName: firstName.trim() }),
    ...(amka.trim() && { amka: amka.trim() }),
  };

  const { patients = [], isLoading: isPatientsLoading } = usePatients(searchParams);

  const filteredPatients = (patients || []).filter((p: PatientDto) => {
    const pLastName = normalizeText(String(p.lastName || ""));
    const pFirstName = normalizeText(String(p.firstName || ""));
    const pAmka = String(p.amka || "").trim();

    return (
      (!lastName.trim() || pLastName.includes(normalizeText(lastName.trim()))) &&
      (!firstName.trim() || pFirstName.includes(normalizeText(firstName.trim()))) &&
      (!amka.trim() || pAmka.includes(amka.trim()))
    );
  });

  const handleSelectPatient = (p: PatientDto) => {
    const pId = Number(p.id ?? (p as any).patientId);
    setLastName(String(p.lastName || ""));
    setFirstName(String(p.firstName || ""));
    setAmka(String(p.amka || ""));
    setIsDropdownOpen(false);

    onSearch({
      firstName: String(p.firstName || ""),
      lastName: String(p.lastName || ""),
      amka: String(p.amka || ""),
      patientId: pId,
    } as SearchFilterState);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    
    const matched = filteredPatients[0];
    const pId = matched ? Number(matched.id ?? (matched as any).patientId) : undefined;

    onSearch({
      firstName,
      lastName,
      amka,
      patientId: pId,
    } as SearchFilterState);
  };

  // 🧹 Συνάρτηση Καθαρισμού Φίλτρων
  const handleReset = () => {
    setLastName("");
    setFirstName("");
    setAmka("");
    setIsDropdownOpen(false);
    // Επαναφέρουμε τα φίλτρα κενά στον γονέα ώστε να φύγει ο πίνακας ασθενούς και να επανέλθει το ημερολόγιο
    onSearch({
      firstName: "",
      lastName: "",
      amka: "",
      patientId: undefined,
    } as SearchFilterState);
  };

  const showResults =
    isDropdownOpen &&
    (lastName.length >= 1 || firstName.length >= 1 || amka.length >= 1);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 relative" ref={dropdownRef}>
      <div>
        <label className="block text-sm font-medium text-gray-700">Επώνυμο</label>
        <input
          type="text"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          className="w-full mt-1 p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="Εισάγετε επώνυμο..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Όνομα</label>
        <input
          type="text"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          className="w-full mt-1 p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="Εισάγετε όνομα..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">ΑΜΚΑ</label>
        <input
          type="text"
          value={amka}
          onChange={(e) => {
            setAmka(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          className="w-full mt-1 p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="Εισάγετε ΑΜΚΑ..."
          maxLength={11}
        />
      </div>

      {showResults && (
        <div className="absolute left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto divide-y divide-gray-100 z-20">
          {isPatientsLoading ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              Αναζήτηση ασθενών...
            </div>
          ) : filteredPatients.length > 0 ? (
            filteredPatients.map((p: PatientDto) => (
              <button
                key={p.id ?? (p as any).patientId}
                type="button"
                onClick={() => handleSelectPatient(p)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 flex justify-between items-center transition-colors"
              >
                <div>
                  <span className="font-semibold text-gray-800">
                    {p.lastName} {p.firstName}
                  </span>
                </div>
                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  AMKA: {p.amka}
                </span>
              </button>
            ))
          ) : (
            <div className="p-3 text-sm text-gray-500 text-center">
              Δε βρέθηκε ασθενής.
            </div>
          )}
        </div>
      )}

      {/* Κουμπιά Υποβολής και Καθαρισμού */}
      <div className="flex flex-col gap-2 pt-2">
        <button
          type="submit"
          className="w-full bg-primary text-white py-2 rounded-lg font-bold hover:bg-darkprimary transition-colors text-sm"
        >
          Αναζήτηση
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-bold hover:bg-gray-200 transition-colors text-sm"
        >
          Καθαρισμός
        </button>
      </div>
    </form>
  );
};

export default SearchFilters;